import complianceStructure from '../assets/policies/compliance.json';

/**
 * Formats a key/text into a human-readable title
 * @param {string} text - The text to format (e.g., 'camelCase', 'snake_case', 'kebab-case')
 * @returns {string} Formatted title
 */
export const formatTitle = (text) => {
  if (!text) return '';

  return text
    .replace(/[-_]/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\s+/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Creates an empty list item based on field definitions
 * @param {Array} fields - Array of field definitions
 * @returns {Object} Empty item with default values
 */
export const createEmptyListItem = (fields) => {
  const emptyItem = {};
  fields.forEach(field => {
    if (field.type === 'object') {
      emptyItem[field.key] = {};
      field.fields.forEach(innerField => {
        if (innerField.type === 'multi-select') {
          emptyItem[field.key][innerField.key] = [];
        } else {
          emptyItem[field.key][innerField.key] = "";
        }
      });
    } else {
      emptyItem[field.key] = field.value || "";
    }
  });
  return emptyItem;
};

// Build a map of documentFieldsSchema field definitions
const docFieldDefs = {};
complianceStructure.fields
  .find(f => f.key === "documentFieldsSchema")
  ?.fields
  ?.forEach(field => {
    docFieldDefs[field.key] = field;
  });

/**
 * Checks if a field's "depends" condition is satisfied
 * @param {Object} item - The item to check
 * @param {Object} fieldDef - Field definition with depends property
 * @returns {boolean} Whether the depends condition is satisfied
 */
export function isDependsSatisfied(item, fieldDef) {
  if (!fieldDef.depends) return true;
  const { key, value } = fieldDef.depends;
  return item[key] === value;
}

/**
 * Cleans a single documentFieldsSchema item by removing empty/default values
 * @param {Object} item - The item to clean
 * @returns {Object} Cleaned item
 */
export function cleanDocFieldItem(item) {
  const cleaned = {};
  for (const [k, v] of Object.entries(item)) {
    const def = docFieldDefs[k];
    if (!def) {
      cleaned[k] = v;
      continue;
    }
    // Remove if depends is not satisfied
    if (def.depends && !isDependsSatisfied(item, def)) continue;
    // Remove empty string
    if (typeof v === "string" && v === "") continue;
    // Remove booleans if value === default
    if (def.type === "checkbox" && typeof v === "boolean" && v === def.default) continue;
    // Remove numbers if value is 0 (except for coordinates/checkbox_coordinates)
    if (
      def.type === "number" &&
      (v === 0 || v === "0") &&
      k !== "coordinates" &&
      k !== "checkbox_coordinates"
    ) continue;
    // Remove arrays if empty
    if (Array.isArray(v) && v.length === 0) continue;
    // Remove objects if empty
    if (typeof v === "object" && v && Object.keys(v).length === 0) continue;
    cleaned[k] = v;
  }
  return cleaned;
}

/**
 * Cleans documentFieldsSchema array
 * @param {Array} arr - Array to clean
 * @returns {Array} Cleaned array
 */
export function cleanDocumentFieldsSchema(arr) {
  return Array.isArray(arr) ? arr.map(cleanDocFieldItem) : arr;
}

/**
 * Recursively removes empty strings, empty arrays/objects
 * @param {*} obj - Value to clean
 * @returns {*} Cleaned value
 */
export function removeEmptyStrings(obj) {
  if (Array.isArray(obj)) {
    return obj.map(removeEmptyStrings);
  } else if (obj && typeof obj === 'object') {
    return Object.entries(obj).reduce((acc, [k, v]) => {
      const cleaned = removeEmptyStrings(v);
      if (
        !(typeof cleaned === 'string' && cleaned === '') &&
        !(Array.isArray(cleaned) && cleaned.length === 0) &&
        !(typeof cleaned === 'object' && cleaned && Object.keys(cleaned).length === 0)
      ) {
        acc[k] = cleaned;
      }
      return acc;
    }, {});
  }
  return obj;
}

/**
 * Filters out empty objects from array
 * @param {Array} arr - Array to filter
 * @returns {Array} Filtered array
 */
export function filterEmptyObjects(arr) {
  if (!Array.isArray(arr)) return arr;
  return arr.filter(obj =>
    obj && typeof obj === "object" && Object.values(obj).some(
      v => v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)
    )
  );
}

/**
 * Gets only the changed fields between original and updated policy
 * @param {Object} original - Original policy data
 * @param {Object} updated - Updated policy data
 * @returns {Object} Object containing only changed fields
 */
export function getChangedFields(original, updated) {
  const result = {
    name: original.name,
    policy: {},
    settings: {},
    users: original.users,
    post_complete_functions: original.post_complete_functions
  };

  if (original.policy && updated.policy) {
    for (const key of Object.keys(updated.policy)) {
      if (
        JSON.stringify(original.policy[key]) !== JSON.stringify(updated.policy[key])
      ) {
        result.policy[key] = updated.policy[key];
      }
    }
  }

  if (original.settings && updated.settings) {
    for (const key in updated.settings) {
      if (
        JSON.stringify(original.settings[key]) !== JSON.stringify(updated.settings[key])
      ) {
        result.settings[key] = updated.settings[key];
      }
    }
  }
  
  if (updated.users && JSON.stringify(original.users) !== JSON.stringify(updated.users)) {
    result.users = updated.users;
  }

  if (updated.post_complete_functions && JSON.stringify(original.post_complete_functions) !== JSON.stringify(updated.post_complete_functions)) {
    result.post_complete_functions = updated.post_complete_functions;
  }

  if (updated.name && original.name !== updated.name) {
    result.name = updated.name;
  }

  return result;
}
